import React, { useState, useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTransition } from 'react-spring';
import useShowStickyNavigation from '../../hooks/useShowStickyNavigation';
import Modal from '../Modal';
import LegalText from '../LegalText';
import {
  Nav,
  NavWrapper,
  ModelInfoContainer,
  ModelName,
  Legal,
  TextContainer,
} from './StickyNavbar.styles';
import CTASection from '../CTASection';
import PriceBlock from './PriceBlock';
import StatusBlock from './StatusBlock';
import { StockCarType } from '../../types/oneGraphTypes';

export interface StickyNavBarProps {
  vehicle: StockCarType;
  market: string;
  digitalLeadsData: string;
  modelYear?: number;
  rapidName: string;
}

const StickyNavBar: React.FC<StickyNavBarProps> = ({
  vehicle,
  market,
  digitalLeadsData,
  modelYear,
  rapidName,
}) => {
  const { id, titleText, cartypeText, salesInfo, dealer, weblink, commissionNumber } =
    (vehicle as StockCarType) ?? {};
  const isAvailableNow = salesInfo?.availableFromDateInfo?.value === 'Immediately';
  const isMarketUSA = market.includes('us');
  const audiCode = vehicle.code?.id ?? '';

  const isTablet = useMediaQuery({ minWidth: 768 });

  const visible = useShowStickyNavigation();

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const onModalOpen = useCallback(() => {
    setModalOpen(true);
  }, []);

  const onModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const transitions = useTransition(visible, null, {
    initial: null,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 150 },
  });

  if (!vehicle) return null;

  return (
    <>
      {transitions.map(
        ({ item, key, props }) =>
          item && (
            <NavWrapper key={key} style={props}>
              <Nav>
                <ModelInfoContainer>
                  {isTablet && (
                    <StatusBlock
                      cartypeText={cartypeText}
                      saleOrderTypeText={salesInfo?.saleOrderTypeText ?? ''}
                      isAvailableNow={isAvailableNow}
                      vehicleOrderStatus={salesInfo?.orderStatusText ?? ''}
                      market={market}
                    />
                  )}
                  {isTablet && <ModelName data-testid="model-name">{titleText}</ModelName>}
                  <PriceBlock
                    onModalOpen={onModalOpen}
                    carPrices={vehicle?.carPrices || []}
                    cartypeText={cartypeText}
                    isAvailableNow={isAvailableNow}
                    market={market}
                  />
                </ModelInfoContainer>
                <CTASection
                  isSticky
                  isMarketUSA={isMarketUSA}
                  kvpsSyncId={dealer?.id?.slice(3)}
                  weblink={weblink}
                  carType={cartypeText}
                  digitalLeadsData={digitalLeadsData}
                  market={market}
                  stockNumber={commissionNumber}
                  modelYear={modelYear || 2025}
                  vehicleId={id}
                  audiCode={audiCode}
                  isAvailableNow={isAvailableNow}
                  rapidName={rapidName}
                />
              </Nav>
            </NavWrapper>
          ),
      )}
      {modalOpen && (
        <Modal onClose={onModalClose} modalOpen={modalOpen}>
          <Legal>
            <TextContainer>
              <LegalText market={market} modelYear={modelYear} />
            </TextContainer>
          </Legal>
        </Modal>
      )}
    </>
  );
};

export default StickyNavBar;
